/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-disable no-underscore-dangle */

/**
 * @import { ContentInfoProps } from "."
 * @import { IdMap, ObjectId } from '../../../util.type'
 * @import { Layout } from '../../layout/layout'
 * @import { Medium } from '../../medium/medium'
 * @import { Playlist } from '../../playlist/playlist'
 */

export class ContentInfo {
  /**
   * @param {ContentInfoProps} contentInfo
   */
  constructor(contentInfo) {
    /**
     * @private
     * @type {number}
     */
    this.contentCounter = -1;

    /** @type {ContentInfoProps['analyticsEnabled']} */
    this.analyticsEnabled = contentInfo.analyticsEnabled;
    /** @type {ContentInfoProps['communicatorSettings']} */
    this.communicatorSettings = contentInfo.communicatorSettings;
    /** @type {ContentInfoProps['backlight']} */
    this.backlight = contentInfo.backlight;
    /** @type {ContentInfoProps['contentTasks']} */
    this.contentTasks = contentInfo.contentTasks;
    /** @type {ContentInfoProps['contentTimestamp']} */
    this.contentTimestamp = contentInfo.contentTimestamp;
    /** @type {ContentInfoProps['deviceName']} */
    this.deviceName = contentInfo.deviceName;
    /** @type {ContentInfoProps['holidayIntervals']} */
    this.holidayIntervals = contentInfo.holidayIntervals;
    /** @type {ContentInfoProps['info']} */
    this.info = contentInfo.info;
    /**
     * @private
     * @type {Map<Medium['id'], Medium>}
     */
    this._media = arrayToMap(contentInfo.media);
    /**
     * @private
     * @type {Map<Layout['id'], Layout>}
     */
    this._layouts = arrayToMap(contentInfo.layouts);
    /** @type {ContentInfoProps['orientationEnum']} */
    this.orientationEnum = contentInfo.orientationEnum;
    /**
     * @private
     * @type {Map<Playlist['id'], Playlist>}
     */
    this._playlists = arrayToMap(contentInfo.playlists);
    /** @type {ContentInfoProps['power']} */
    this.power = contentInfo.power;
    /** @type {ContentInfoProps['rebootTasks']} */
    this.rebootTasks = contentInfo.rebootTasks;
    /** @type {ContentInfoProps['serverUrl']} */
    this.serverUrl = contentInfo.serverUrl;
    /** @type {ContentInfoProps['stretchEnum']} */
    this.stretchEnum = contentInfo.stretchEnum;
    /** @type {ContentInfoProps['visualController']} */
    this.visualController = contentInfo.visualController;
    /** @type {ContentInfoProps['volume']} */
    this.volume = contentInfo.volume;
  }

  /**
   * @returns {ReadonlyMap<Medium['id'], Medium>}
   */
  get media() {
    return this._media;
  }

  /**
   * @returns {ReadonlyMap<Layout['id'], Layout>}
   */
  get layouts() {
    return this._layouts;
  }

  /**
   * @returns {ReadonlyMap<Playlist['id'], Playlist>}
   */
  get playlists() {
    return this._playlists;
  }

  /**
   * @param {Omit<Medium, 'id'>} mediumWithoutId
   * @returns {Medium}
   */
  addTransientMedium(mediumWithoutId) {
    const id = this.nextId();
    const m = { ...mediumWithoutId, id };
    this._media.set(id, m);
    return m;
  }

  /**
   * @param {Omit<Layout, 'id'>} layoutWithoutId
   * @returns {Layout}
   */
  addTransientLayout(layoutWithoutId) {
    const id = this.nextId();
    const l = { ...layoutWithoutId, id };
    this._layouts.set(id, l);
    return l;
  }

  /**
   * @param {Omit<Playlist, 'id'>} playlistWithoutId
   * @returns {Playlist}
   */
  addTransientPlaylist(playlistWithoutId) {
    const id = this.nextId();
    const p = { ...playlistWithoutId, id };
    this._playlists.set(id, p);
    return p;
  }

  /**
   * @param {Medium} medium
   * @returns {boolean}
   */
  deleteTransientMedium(medium) {
    return this._media.delete(medium.id);
  }

  /**
   * @param {Layout} layout
   * @returns {boolean}
   */
  deleteTransientLayout(layout) {
    return this._layouts.delete(layout.id);
  }

  /**
   * @param {Playlist} playlist
   * @returns {boolean}
   */
  deleteTransientPlaylist(playlist) {
    return this._playlists.delete(playlist.id);
  }

  /**
   * @private
   * @returns {string}
   */
  nextId() {
    this.contentCounter -= 1;
    return this.contentCounter.toString();
  }
}

/**
 * @template { ObjectId } T
 * @param {Array<T>} array
 * @returns {IdMap<T>}
 */
function arrayToMap(array) {
  return array.reduce(
    (map, item) => map.set(item.id, item),
    /** @type {IdMap<T>} */ (new Map())
  );
}
